/**
 * Apple sign-in on Android, minus the Capacitor plumbing.
 *
 * There is no native Apple SDK on Android, so the app opens Apple's web
 * authorize page in a Custom Tab and waits for our `apple-native-callback`
 * endpoint to bounce back to `sellwildapp://auth/apple?...`. Building that
 * authorize URL and reading the deep link are pure string work; they lived
 * inside a Promise inside an event listener inside `useLoginAuth`, where
 * nothing could reach them without a fake Capacitor runtime.
 */

/** The deep link `pages/api/auth/apple-native-callback` redirects the Custom Tab to. */
export const APPLE_DEEP_LINK_PREFIX = 'sellwildapp://auth/apple';

/** Apple's Service ID for the web flow, when the build did not inline one. */
export const DEFAULT_APPLE_SERVICE_ID = 'com.sellwild.www';

/** The API origin a Capacitor build posts Apple's form_post to, when none is configured. */
export const DEFAULT_APPLE_API_ORIGIN = 'https://sellwild.com';

/** What the deep link carries, forwarded to NextAuth's native-oauth provider. */
export interface AppleDeepLinkToken {
  idToken: string;
  email?: string;
  name?: string;
}

/**
 * "Ada Lovelace" from its parts, or undefined when Apple sent neither — it sends
 * the name on the first authorization only.
 */
export function joinNameParts(first?: string | null, last?: string | null): string | undefined {
  return [first, last].filter(Boolean).join(' ') || undefined;
}

/** Apple's authorize URL for the Android Custom Tab flow. */
export function appleAuthorizeUrl(options: {
  serviceId?: string | null;
  apiOrigin?: string | null;
  state: string;
  nonce: string;
}): string {
  const url = new URL('https://appleid.apple.com/auth/authorize');
  url.searchParams.set('client_id', options.serviceId || DEFAULT_APPLE_SERVICE_ID);
  url.searchParams.set(
    'redirect_uri',
    `${options.apiOrigin || DEFAULT_APPLE_API_ORIGIN}/api/auth/apple-native-callback`,
  );
  url.searchParams.set('response_type', 'code id_token');
  url.searchParams.set('response_mode', 'form_post');
  url.searchParams.set('scope', 'email name');
  url.searchParams.set('state', options.state);
  url.searchParams.set('nonce', options.nonce);
  return url.toString();
}

/** Whether an opened app URL is the Apple callback, rather than some other deep link. */
export function isAppleDeepLink(url: string): boolean {
  return url.startsWith(APPLE_DEEP_LINK_PREFIX);
}

/**
 * Read the token out of Apple's callback deep link. Throws on anything that is
 * not a completed sign-in for THIS attempt: Apple's own error, a state that does
 * not match the one we sent (a stale or forged link), or no id_token.
 */
export function readAppleDeepLink(url: string, expectedState: string): AppleDeepLinkToken {
  const params = new URL(url).searchParams;

  const appleError = params.get('error');
  if (appleError) throw new Error(appleError);
  if (params.get('state') !== expectedState) throw new Error('Apple sign-in state mismatch');

  const idToken = params.get('id_token');
  if (!idToken) throw new Error('Apple did not return an id_token');

  // Apple sends `user` (email + name) on the first authorization only, as JSON.
  // A malformed one costs the name and email hints, never the sign-in.
  const userJson = params.get('user');
  if (!userJson) return { idToken };
  try {
    const user = JSON.parse(userJson) as {
      email?: string;
      name?: { firstName?: string; lastName?: string };
    };
    return { idToken, email: user.email, name: joinNameParts(user.name?.firstName, user.name?.lastName) };
  } catch {
    return { idToken };
  }
}
